import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { useParams } from "react-router-dom";
import { z } from "zod";

import {
    ArrayOfAssessmentDto as GetRiskInsightsDto,
    RiskDirectoryRootNodeDelta,
    StandardNodeDelta,
} from "api/generated/model";

import { RISK_MODULE } from "../../url";

import { Delta } from "./mapInsightsModel";

export function normalizeNullableDelta<T>(
    delta:
        | { type: "Unchanged"; value?: T | null }
        | { type: "Changed"; start?: T | null; end?: T | null }
): Delta<T | null> {
    switch (delta.type) {
        case "Unchanged":
            return { type: "Unchanged", value: delta.value ?? null };
        case "Changed":
            return {
                type: "Changed",
                start: delta.start ?? null,
                end: delta.end ?? null,
            };
        default:
            return assertNever(delta);
    }
}

// Extract a single value from delta, the newer (end) one if changed.
export function extractDeltaValue<T>(delta: Delta<T>): T {
    switch (delta.type) {
        case "Unchanged":
            return delta.value;
        case "Changed":
            return delta.end;
        default:
            assertNever(delta);
    }
}

export function extractStartValue<T>(delta: Delta<T>): T {
    switch (delta.type) {
        case "Unchanged":
            return delta.value;
        case "Changed":
            return delta.start;
        default:
            assertNever(delta);
    }
}

// Extract all values in the delta, start first.
export function extractValues<T>(delta: Delta<T>): T[] {
    switch (delta.type) {
        case "Unchanged":
            return [delta.value];
        case "Changed":
            return [delta.start, delta.end];
        default:
            assertNever(delta);
    }
}

export function mapDeltaValue<T, U>(
    delta: Delta<T>,
    mapFn: (value: T) => U
): Delta<U> {
    switch (delta.type) {
        case "Unchanged":
            return {
                type: "Unchanged",
                value: mapFn(delta.value),
            };
        case "Changed":
            return {
                type: "Changed",
                start: mapFn(delta.start),
                end: mapFn(delta.end),
            };
        default:
            assertNever(delta);
    }
}

export interface RiskNodeDelta extends Extract<
    StandardNodeDelta,
    { type: "Risk" }
> {
    path: string[];
}

export function extractRiskNodes(
    node: RiskDirectoryRootNodeDelta
): RiskNodeDelta[] {
    function extractRiskNodesInner(
        node: StandardNodeDelta,
        path: Delta<string>[]
    ): RiskNodeDelta[] {
        switch (node.type) {
            case "Risk":
                return [{ ...node, path: path.map(extractDeltaValue) }];
            case "Process":
                return node.nodes.flatMap((childNode) =>
                    extractRiskNodesInner(childNode, [...path, node.name])
                );
        }
    }

    return node.nodes.flatMap((childNode) =>
        extractRiskNodesInner(childNode, [node.name])
    );
}

// Could be made generic with an extract children function but that's overkill for now.
export function foldTree<T>(
    nodes: StandardNodeDelta[],
    init: T,
    f: (acc: T, item: StandardNodeDelta) => T
): T {
    let acc = init;
    for (const node of nodes) {
        acc = f(acc, node);
        if (node.type === "Process") {
            acc = foldTree(node.nodes, acc, f);
        }
    }
    return acc;
}

const paramSchema = z.object({
    id: z.coerce.number().int().positive(),
    entityId: z.coerce.number().int().positive().optional(),
});

export function useMapInsightsParams(): z.infer<typeof paramSchema> {
    const params = useParams();
    return paramSchema.parse(params);
}

export function getTitle(
    linkedResources: GetRiskInsightsDto["linkedResources"],
    id: number,
    entityId: number | undefined,
    t: TFunction
) {
    const riskModuleBreadCrumb = {
        label: t("RiskDirectory_RiskManager"),
        url: RISK_MODULE,
    };
    const riskDirectoryTitle = extractDeltaValue(linkedResources.rootNode.name);
    if (entityId !== undefined) {
        const focusedEntityResource = linkedResources.entities.find(
            (entity) => entity.id === entityId
        );
        if (!focusedEntityResource) {
            throw new Error("Expected to find directory associated entity");
        }
        return {
            title: extractDeltaValue(focusedEntityResource.name),
            breadCrumbs: [
                riskModuleBreadCrumb,
                {
                    label: riskDirectoryTitle,
                    url: `${RISK_MODULE}/${id}`,
                },
            ],
        };
    }

    return {
        title: riskDirectoryTitle,
        breadCrumbs: [riskModuleBreadCrumb],
    };
}
